/**
 * ShareButton — «Отправить в чат»: the track on «Сейчас играет», sent to somebody by Telegram's
 * own dialog.
 *
 * Task 15. `spec.md` «Бот»: «Отправка трека из приложения: сервер готовит сообщение
 * (`savePreparedInlineMessage`), приложение вызывает системный диалог отправки Telegram
 * (`shareMessage`). Отказ пользователя ошибкой не считается». One tap: the server prepares the
 * message (`apps/api/src/routes/share.ts`) — the same one `@бот` sends, the track and a button
 * that opens it in the player — and Telegram asks whom to send it to.
 *
 * ## What counts as a failure
 *
 * | что случилось | что видно |
 * |---|---|
 * | сообщение ушло | ничего: Telegram показывает своё |
 * | человек закрыл диалог | ничего — он передумал, это не сбой |
 * | сервер не подготовил сообщение, нет сети | строка {@link SHARE_FAILED_TEXT} |
 * | клиент не открыл диалог | та же строка |
 *
 * The line is drawn by the screen, in the place of the artist under the title
 * (`screens/NowPlaying.tsx`): that row has a declared height, so the screen still fits an iPhone
 * SE, and it is where the loading line already appears. This button only reports.
 *
 * ## No button where it cannot work
 *
 * A client older than 8.0, and the browser of the development mode, cannot open the dialog, and
 * the button is not drawn: a button that does nothing is worse than none.
 *
 * ## In the track card too (task 50)
 *
 * The card of any track carries the same button, with its words written out like the card's other
 * buttons (`withLabel`), for the card's track — not the one playing (`spec.md` story 144). A failure
 * there is the card's own status line.
 *
 * ## One request per tap
 *
 * The button is off while the message is being prepared, and on again the moment the dialog is
 * asked to open — not when it closes: a dialog that never answers must not leave it stuck.
 */

import type { JSX } from 'react';
import { useState } from 'react';

import { prepareShare } from '../lib/api';
import { telegram } from '../lib/telegram';
import { useSession } from '../store/session';

export const SHARE_LABEL = 'Отправить в чат';

/**
 * The one line a failed share leaves. Short enough for the artist's line of a 320-pixel phone:
 * the first wording, «…Попробуйте ещё раз.», was cut to «…П…» at 375 pixels in the browser. What
 * to do needs no words — the button is beside it.
 */
export const SHARE_FAILED_TEXT = 'Не удалось отправить';

export interface ShareButtonProps {
  readonly trackId: number;
  /** `true` when the share failed, `false` at the next tap — see «What counts as a failure». */
  readonly onFailedChange: (failed: boolean) => void;
  readonly className: string;
  readonly iconClassName: string;
  /** Task 50: the words beside the icon — in the track card, where every button says itself. */
  readonly withLabel?: boolean | undefined;
  /**
   * Task 71: shorter words for a narrow button — «В чат» under the icon in the card. The name a
   * screen reader hears stays «Отправить в чат», which says them too.
   */
  readonly shortLabel?: string | undefined;
}

export function ShareButton({
  trackId,
  onFailedChange,
  className,
  iconClassName,
  withLabel = false,
  shortLabel,
}: ShareButtonProps): JSX.Element | null {
  const [busy, setBusy] = useState(false);
  if (!telegram.canShareMessage()) return null;

  const share = async (): Promise<void> => {
    setBusy(true);
    onFailedChange(false);
    const prepared = await prepareShare(useSession.getState().session?.token, trackId);
    setBusy(false);
    if (!prepared.ok) {
      onFailedChange(true);
      return;
    }
    const outcome = await telegram.shareMessage(prepared.data.id);
    if (outcome === 'unavailable') onFailedChange(true);
  };

  return (
    <button
      type="button"
      className={className}
      aria-label={withLabel && shortLabel === undefined ? undefined : SHARE_LABEL}
      disabled={busy}
      aria-busy={busy}
      onClick={() => {
        void share();
      }}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" className={iconClassName}>
        <path
          d="M21 3L3 10.5l7 2.5 2.5 7L21 3zM10 13l5-5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
      {withLabel && (shortLabel === undefined ? SHARE_LABEL : <span>{shortLabel}</span>)}
    </button>
  );
}
